import Markdown from 'markdown-to-jsx';
import React, { forwardRef } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import AvatarGroup from '@atlaskit/avatar-group';
import Link from '@atlaskit/link';

import { AIContentBlur, AITelepointerWrapper } from '@atlassian/ai-components';
import type { ContentUpdateType, ContentTimeframeLength } from '@atlassian/convo-ai-api';

import { AISummaryFooter, FeedbackCollectorPropertiesProvider } from '@confluence/quick-summary';

import { EditorsNameListComponent, type Editor } from './EditorsNameListComponent';
import { VersionDiffEmptyComponent } from './VersionDiffEmptyComopnent';
import { type FollowUpObject } from '@atlassian/ai-agent-streaming';
import { PageCatchupFollowUpContainer } from '../PageCatchupFollowUpContainer';
import { fg } from '@confluence/feature-gating';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ScrollableBody = styled.div({
	font: token('font.body.large'),
	overflowY: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&::-webkit-scrollbar': {
		width: token('space.100'),
		background: 'transparent',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&::-webkit-scrollbar-thumb': {
		borderRadius: token('space.100'),
		backgroundColor: token('color.background.neutral'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
});

const HeaderStyles = xcss({
	backgroundColor: 'color.background.accent.gray.subtlest',
	borderRadius: '4px',
	padding: 'space.100',
});

const StackStyles = xcss({
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
	height: '100%',
});

const ContentStyles = xcss({
	gap: 'space.200',
	height: '100%',
});

const ContentHeightStyles = xcss({
	maxHeight: '350px',
});

const FooterObjectSidebarStyles = xcss({
	// Object sidebar padding is 16px, this ensures that the footer isn't cutoff by parent padding
	width: 'calc(100% + 32px)',
	marginLeft: 'space.negative.200',
});

const i18n = defineMessages({
	lastVisitedOn: {
		id: 'page-catchup.last-visited-on-text',
		defaultMessage: 'Changes since you last visited on {lastVisitedOnDate}.',
		description: 'Text for when the user last visited the content',
	},
	firstTimeVisit: {
		id: 'page-catchup.first-time-visit-text',
		defaultMessage:
			'Since this is your first time visiting this content, you’re seeing a page summary instead of a summary of changes.',
		description:
			'Text to show when a user views the feature but is visiting the page for the first time',
	},
	noChangesSinceLastVisit: {
		id: 'page-catchup.no-changes-since-last-visit-text',
		defaultMessage:
			'There haven’t been any changes since your last visit on {lastVisitedOnDate}. We’ve generated this summary instead to help refresh your memory.',
		description:
			'Text to show when a user views the feature but is visiting the page for the first time',
	},
	fullVersionHistoryLinkText: {
		id: 'page-catchup.full.version.history.link.text',
		defaultMessage: 'See changes in version history',
		description: 'Link text to direct a user to the full version history page',
	},
	changesBetweenVersionsText: {
		id: 'page-catchup.changes.between.versions.text',
		defaultMessage: 'Changes between selected versions',
		description:
			'Text to show when a user is viewing the diff between two selected versions of a page',
	},
	changesWithinTimeframeHoursText: {
		id: 'page-catchup.changes.within.timeframe.hours.text',
		defaultMessage: 'Changes within the last {numHours} hours.',
		description: 'Text to show a user changes within a timeframe of hours',
	},
	noChangesWithinTimeframeHoursText: {
		id: 'page-catchup.no.changes.within.timeframe.hours.text',
		defaultMessage:
			'There haven’t been any changes within the last {numHours} hours. We’ve generated this summary instead to help refresh your memory.',
		description: 'Text to show a user that there are no changes within a timeframe of hours',
	},
	changesWithinTimeframeDaysText: {
		id: 'page-catchup.changes.within.timeframe.days.text',
		defaultMessage: 'Changes within the last {numDays} days.',
		description: 'Text to show a user changes within a timeframe of days',
	},
	noChangesWithinTimeframeDaysText: {
		id: 'page-catchup.no.changes.within.timeframe.days.text',
		defaultMessage:
			'There haven’t been any changes within the last {numDays} days. We’ve generated this summary instead to help refresh your memory.',
		description: 'Text to show a user that there are no changes within a timeframe of days',
	},
	allTimeChangesText: {
		id: 'page-catchup.all.time.changes.text',
		defaultMessage: 'All time changes.',
		description: 'Text to show a user all changes in document history',
	},
	noChangesAllTimeText: {
		id: 'page-catchup.no.changes.all.time.text',
		defaultMessage:
			'There haven’t been any changes. We’ve generated this summary instead to help refresh your memory.',
		description: 'Text to show when a user is viewing a document that only has one version',
	},
});

type PageCatchupComponentProps = {
	contentId: string;
	content: string;
	contentType: string;
	spaceKey: string;
	lastVisitedOnDate: string | undefined;
	editors: Editor[];
	hideMetadata?: boolean;
	versionHistoryLink?: string | undefined;
	onVersionHistoryLinkClick?: () => void;
	hasVersionChangedSinceLastVisit?: boolean;
	onShowMoreClick?: () => void;
	source?: string;
	isVersionDiffEmpty?: boolean;
	isInObjectSidebar?: boolean;
	updateType: ContentUpdateType;
	timeframeLength?: ContentTimeframeLength;
	followUpContent?: FollowUpObject[];
	isRovoEnabled: boolean;
	setIsOpen: (open: boolean) => void;
	contentPath: string;
	showFooter?: boolean;
};

export const PageCatchupComponent = forwardRef<HTMLDivElement, PageCatchupComponentProps>(
	(
		{
			contentId,
			content,
			contentType,
			spaceKey,
			lastVisitedOnDate,
			editors,
			hideMetadata,
			versionHistoryLink,
			onVersionHistoryLinkClick,
			hasVersionChangedSinceLastVisit,
			onShowMoreClick,
			source,
			isVersionDiffEmpty,
			isInObjectSidebar,
			updateType,
			timeframeLength,
			followUpContent,
			isRovoEnabled,
			setIsOpen,
			contentPath,
			showFooter = true,
		},
		catchUpContentRef,
	) => {
		const getHeaderText = () => {
			if (updateType === 'BETWEEN_VERSIONS' && source === 'pageHistory' && !isVersionDiffEmpty) {
				return <FormattedMessage {...i18n.changesBetweenVersionsText} />;
			}

			if (updateType === 'SINCE_X_DAYS' && timeframeLength) {
				switch (timeframeLength) {
					case 'ALL_TIME':
						const allTimeKey = hasVersionChangedSinceLastVisit
							? 'allTimeChangesText'
							: 'noChangesAllTimeText';
						return <FormattedMessage {...i18n[allTimeKey]} />;
					case 'ONE_DAY_AGO':
						const dayKey = hasVersionChangedSinceLastVisit
							? 'changesWithinTimeframeHoursText'
							: 'noChangesWithinTimeframeHoursText';
						return <FormattedMessage {...i18n[dayKey]} values={{ numHours: 24 }} />;
					case 'ONE_WEEK_AGO':
						const weekKey = hasVersionChangedSinceLastVisit
							? 'changesWithinTimeframeDaysText'
							: 'noChangesWithinTimeframeDaysText';
						return <FormattedMessage {...i18n[weekKey]} values={{ numDays: 7 }} />;
					case 'TWO_WEEKS_AGO':
						const twoWeekKey = hasVersionChangedSinceLastVisit
							? 'changesWithinTimeframeDaysText'
							: 'noChangesWithinTimeframeDaysText';
						return <FormattedMessage {...i18n[twoWeekKey]} values={{ numDays: 14 }} />;
					case 'ONE_MONTH_AGO':
						const monthKey = hasVersionChangedSinceLastVisit
							? 'changesWithinTimeframeDaysText'
							: 'noChangesWithinTimeframeDaysText';
						return <FormattedMessage {...i18n[monthKey]} values={{ numDays: 30 }} />;
				}
			}

			if (updateType === 'SINCE_LAST_VIEWED_MARKDOWN') {
				if (!lastVisitedOnDate) {
					return <FormattedMessage {...i18n.firstTimeVisit} />;
				}

				const messageKey = hasVersionChangedSinceLastVisit
					? 'lastVisitedOn'
					: 'noChangesSinceLastVisit';
				return <FormattedMessage {...i18n[messageKey]} values={{ lastVisitedOnDate }} />;
			}
		};

		if (source === 'pageHistory' && isVersionDiffEmpty) {
			return <VersionDiffEmptyComponent />;
		}

		return (
			<FeedbackCollectorPropertiesProvider entrypointId="d4c9238b-d3d6-4298-83d9-10f6bddd4f2b">
				<Stack xcss={StackStyles} spread="space-between">
					<Box padding={isInObjectSidebar ? 'space.100' : 'space.200'}>
						<Stack space="space.200">
							<Stack xcss={HeaderStyles} space="space.100">
								<Box>{getHeaderText()}</Box>
								{((lastVisitedOnDate && hasVersionChangedSinceLastVisit) ||
									source === 'pageHistory') &&
									editors.length > 0 && (
										<Inline space="space.050" alignBlock="center">
											<AvatarGroup size="small" data={editors} testId="page-catchup" maxCount={4} />
											<EditorsNameListComponent editors={editors} />
										</Inline>
									)}
							</Stack>
							{(source !== 'pageHistory' || !isVersionDiffEmpty) && (
								<Flex
									direction="column"
									xcss={[ContentStyles, !isInObjectSidebar && ContentHeightStyles]}
								>
									<AIContentBlur
										shouldOpenInCollapsedView={
											(!lastVisitedOnDate || !hasVersionChangedSinceLastVisit) &&
											source !== 'pageHistory' &&
											!isInObjectSidebar
										}
										onShowMore={onShowMoreClick}
										summaryLineHeight={80}
									>
										<ScrollableBody ref={catchUpContentRef}>
											<AITelepointerWrapper hideTelepointer={!hideMetadata}>
												<Markdown>{content}</Markdown>
											</AITelepointerWrapper>
										</ScrollableBody>
										<PageCatchupFollowUpContainer
											content={content}
											contentPath={contentPath}
											setIsOpen={setIsOpen}
											isPageCatchupFollowUpEnabled={
												isRovoEnabled && fg('page-catchup-follow-up-enabled')
											}
											followUpContent={followUpContent}
										/>
									</AIContentBlur>
								</Flex>
							)}
							{versionHistoryLink && !hideMetadata && hasVersionChangedSinceLastVisit && (
								<Link href={versionHistoryLink} onClick={onVersionHistoryLinkClick} target="_blank">
									<FormattedMessage {...i18n.fullVersionHistoryLinkText} />
								</Link>
							)}
						</Stack>
					</Box>
					{!hideMetadata && showFooter && (
						<Box xcss={isInObjectSidebar && FooterObjectSidebarStyles}>
							<AISummaryFooter
								analyticsProperties={{ source: 'pageCatchup' }}
								contentId={contentId}
								spaceKey={spaceKey}
								contentType={contentType}
								summaryText={content}
							/>
						</Box>
					)}
				</Stack>
			</FeedbackCollectorPropertiesProvider>
		);
	},
);
